import React, { useEffect, useState } from "react";
import { ModernContentGrid } from "../FolderView/components/ModernContentGrid";
import { ModernActionBar } from "../FolderView/components/ModernActionBar";
import { useProjectRootContent } from "./hooks/useProjectRootContent";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Breadcrumb, createProjectBreadcrumb } from "@/components/ui/breadcrumb";
import { FolderOpen, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ProjectShareButton from "@/components/Auth/Projects/ProjectShareButton";
import { useAuth } from "@/context/auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

interface ProjectRootViewProps {
  projectId: string;
  onSelectFolder: (folderId: string) => void;
  onStartAssessment: (projectId: string, folderId: string) => void;
  onNavigateToAssessment: (assessmentId: string, folderId: string) => void;
  onCreateNewAssessment: (projectId: string, folderId: string) => void;
  refreshFolderTree?: () => void;
}

const ProjectRootView: React.FC<ProjectRootViewProps> = ({
  projectId,
  onSelectFolder,
  onStartAssessment,
  onNavigateToAssessment,
  onCreateNewAssessment,
  refreshFolderTree
}) => {
  const navigate = useNavigate();
  const {
    project,
    folders,
    assessments,
    isLoading,
    error,
    refetch
  } = useProjectRootContent({ projectId });

  const { user } = useAuth();
  const [pendingShareId, setPendingShareId] = useState<string | null>(null);

  // State for creating a new risk assessment from project root
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedFolderForNew, setSelectedFolderForNew] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Basic permission handling: owner or accepted collaborator
  const isOwner = !!(project && user && (project as any).user_id === user.id);
  const [isCollaborator, setIsCollaborator] = useState(false);
  const canCreate = isOwner || isCollaborator;

  useEffect(() => {
    const checkPendingShare = async () => {
      if (!user || project || isLoading) return;
      const orFilters = [
        `and(shared_with_email.eq.${JSON.stringify(user.email)},accepted.is.null)`,
        `and(shared_with_email.eq.${JSON.stringify(user.email)},accepted.eq.false)`,
        `and(shared_with_id.eq.${user.id},accepted.is.null)`,
        `and(shared_with_id.eq.${user.id},accepted.eq.false)`
      ].join(",");
      const { data, error } = await supabase
        .from("project_shares")
        .select("id")
        .eq("project_id", projectId)
        .or(orFilters)
        .limit(1);
      if (!error && data && data.length > 0) {
        setPendingShareId(data[0].id);
      } else {
        setPendingShareId(null);
      }
    };
    checkPendingShare();
  }, [user, project, isLoading, projectId]);

  // Determine if current user is an accepted collaborator on this project
  useEffect(() => {
    const checkCollaborator = async () => {
      if (!user || !projectId) return;
      const orFilters = [
        `shared_with_id.eq.${user.id}`,
        user.email ? `shared_with_email.eq.${JSON.stringify(user.email)}` : null
      ].filter(Boolean).join(",");

      const { data, error } = await supabase
        .from("project_shares")
        .select("id, accepted")
        .eq("project_id", projectId)
        .or(orFilters)
        .eq("accepted", true)
        .limit(1);

      setIsCollaborator(!error && !!data && data.length > 0);
    };

    checkCollaborator();
  }, [user, projectId]);

  const handleAcceptInline = async () => {
    if (!pendingShareId || !user?.id) return;
    const { error } = await supabase
      .from("project_shares")
      .update({ accepted: true, shared_with_id: user.id })
      .eq("id", pendingShareId);
    if (error) {
      toast.error("Kunde inte acceptera delningen");
      return;
    }
    toast.success("Delningen accepterades");
    setPendingShareId(null);
    await refetch();
  };

  const handleRejectInline = async () => {
    if (!pendingShareId) return;
    const { error } = await supabase
      .from("project_shares")
      .delete()
      .eq("id", pendingShareId);
    if (error) {
      toast.error("Kunde inte avvisa delningen");
      return;
    }
    toast.success("Delningen avvisades");
    setPendingShareId(null);
    navigate('/uppdrag');
  };
  const handleNavigateToSharedFolders = () => {
    navigate('/uppdrag');
    // Small delay to ensure navigation completes before setting active tab
    setTimeout(() => {
      const event = new CustomEvent('setActiveSubTab', { detail: 'shared-folders' });
      window.dispatchEvent(event);
    }, 100);
  };

  // Create assessment flow from project root
  const openCreateAssessmentDialog = () => {
    setSelectedFolderForNew(null);
    setIsCreateDialogOpen(true);
  };

  const findExistingDefaultFolder = (): string | null => {
    const defaultFolder = folders?.find((f: any) => f?.name?.toLowerCase() === 'allmänt' && !f?.parent_folder_id);
    return defaultFolder?.id ?? null;
  };

  const createDefaultFolder = async (): Promise<string | null> => {
    try {
      if (!user) {
        toast.error("Du måste vara inloggad för att skapa mappar");
        return null;
      }
      const { data, error } = await supabase
        .from('folders')
        .insert({
          name: 'Allmänt',
          description: null,
          project_id: projectId,
          parent_folder_id: null,
          created_by: user.id
        })
        .select('id')
        .single();

      if (error) throw error;

      toast.success('Mappen "Allmänt" har skapats!');
      await refetch();
      refreshFolderTree?.();
      return data?.id as string;
    } catch (e: any) {
      console.error('Error creating default folder:', e);
      toast.error('Kunde inte skapa standardmappen', {
        description: e?.message || 'Ett oväntat fel uppstod'
      });
      return null;
    }
  };

  const handleConfirmNewAssessment = async () => {
    if (!projectId) return;
    setCreating(true);
    try {
      let folderId = selectedFolderForNew;
      if (!folderId) {
        folderId = findExistingDefaultFolder() || await createDefaultFolder();
      }
      if (folderId) {
        onCreateNewAssessment(projectId, folderId);
        setIsCreateDialogOpen(false);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCancelNewAssessment = () => {
    setIsCreateDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Laddar projektinnehåll...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Card className="p-6 max-w-md text-center">
          <p className="text-destructive mb-4">Kunde inte ladda projektinnehåll</p>
          <p className="text-sm text-muted-foreground">{error}</p>
        </Card>
      </div>
    );
  }

  if (!project && pendingShareId) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Card className="p-6 max-w-md text-center">
          <h3 className="text-lg font-semibold mb-2">Delning väntar på ditt godkännande</h3>
          <p className="text-sm text-muted-foreground mb-4">Acceptera delningen för att se projektet.</p>
          <div className="flex items-center justify-center gap-2">
            <Button onClick={handleAcceptInline} className="bg-green-600 hover:bg-green-700">Acceptera</Button>
            <Button variant="outline" onClick={handleRejectInline} className="border-red-300 text-red-700 hover:bg-red-50">Avvisa</Button>
          </div>
        </Card>
      </div>
    );
  }

  const handleOpenFolder = async (folderId: string): Promise<void> => {
    onSelectFolder(folderId);
  };

  const handleCreateFolder = async (name: string, description?: string): Promise<void> => {
    try {
      if (!user) {
        toast.error("Du måste vara inloggad för att skapa mappar");
        return;
      }
      const { data, error } = await supabase
        .from('folders')
        .insert({
          name: name.trim(),
          description: description ?? null,
          project_id: projectId,
          parent_folder_id: null,
          created_by: user.id
        })
        .select()
        .single();

      if (error) throw error;

      toast.success(`Mapp "${name}" har skapats!`);
      await refetch();
      refreshFolderTree?.();
    } catch (e: any) {
      console.error('Error creating root folder:', e);
      toast.error('Kunde inte skapa mappen', {
        description: e?.message || 'Ett oväntat fel uppstod'
      });
    }
  };
  const handleDeleteFolder = async (folderId: string): Promise<void> => {
    console.log("Delete folder:", folderId);
    // Implement delete logic if needed
  };

  const handleDeleteAssessment = async (assessmentId: string): Promise<void> => {
    console.log("Delete assessment:", assessmentId);
    // Implement delete logic if needed
  };

  const handleNavigateToAssessmentWithFolder = (assessmentId: string) => {
    // For project-level assessments, we need to find the folder
    // For now, we'll use the project ID as folder ID or handle differently
    onNavigateToAssessment(assessmentId, projectId);
  };

  const handleStartAssessmentForProject = () => {
    // For starting assessment at project level, we might need a default folder
    // or handle this differently based on business logic
    console.log("Start assessment for project:", projectId);
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Project Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm">
        <div className="container max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <Breadcrumb items={createProjectBreadcrumb(project?.name || "Projekt")} />
              {project?.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {project.description}
                </p>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {canCreate && (
                <Button size="sm" onClick={openCreateAssessmentDialog} className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Ny risksammanställning
                </Button>
              )}
              <ProjectShareButton 
                projectId={projectId} 
                projectName={project?.name || "Projekt"} 
              />
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNavigateToSharedFolders}
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              >
                <FolderOpen className="h-4 w-4" />
                Se delade mappar
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <ModernActionBar
        onCreateFolder={handleCreateFolder}
        projectId={projectId}
        folderId="" // Empty string for project root
        isShared={false}
        onRefresh={() => { refetch(); refreshFolderTree?.(); }}
      />

      {/* Content Grid */}
      <div className="flex-1 container max-w-7xl mx-auto px-6 py-6">
        <ModernContentGrid
          folderData={null} // No parent folder for project root
          subfolders={folders}
          assessments={assessments}
          projectId={projectId}
          folderId={null} // No specific folder for project root
          isShared={false}
          onOpenFolder={handleOpenFolder}
          onNavigateToAssessment={handleNavigateToAssessmentWithFolder}
          onDeleteFolder={handleDeleteFolder}
          onDeleteAssessment={handleDeleteAssessment}
          onCreateFolder={handleCreateFolder}
          onStartAssessment={handleStartAssessmentForProject}
        />
      </div>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ny risksammanställning</DialogTitle>
            <DialogDescription>Välj en mapp eller skapa i standardmappen "Allmänt".</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {folders && folders.length > 0 ? (
              <div className="max-h-60 overflow-auto rounded-md border">
                {folders.map((f: any) => (
                  <button
                    key={f.id}
                    onClick={() => setSelectedFolderForNew(f.id)}
                    className={`w-full text-left px-3 py-2 hover:bg-accent ${selectedFolderForNew === f.id ? 'bg-accent' : ''}`}
                  >
                    <div className="font-medium">{f.name}</div>
                    {f.description && <div className="text-sm text-muted-foreground">{f.description}</div>}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Inga mappar hittades i projektet.</p>
            )}

            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => setSelectedFolderForNew(null)}>
                Skapa i "Allmänt"
              </Button>
              <div className="ml-auto flex gap-2">
                <Button variant="outline" onClick={handleCancelNewAssessment}>Avbryt</Button>
                <Button onClick={handleConfirmNewAssessment} disabled={creating}>
                  {creating ? 'Skapar...' : 'Fortsätt'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectRootView;